// Ganchos de interface compartilhados: media query, relógio, preferência local e o título da página para a moldura.
import { createContext, useContext, useEffect, useState } from 'react';

/** Barra lateral e gaveta do painel a partir de 1024px; quadro em colunas a partir de 640px. */
export const MQ_DESKTOP = '(min-width: 1024px)';
export const MQ_TABLET = '(min-width: 640px)';

/** `matchMedia` como estado; começa com o valor real (sem flash) e acompanha mudanças de janela. */
export function useMediaQuery(query: string): boolean {
  const [ok, setOk] = useState(() => (typeof window !== 'undefined' ? window.matchMedia(query).matches : false));
  useEffect(() => {
    const mq = window.matchMedia(query);
    const onChange = () => setOk(mq.matches);
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [query]);
  return ok;
}

/** Re-renderiza a cada `ms` (relógio dos "atualizado há N min"). */
export function useTick(ms = 60_000): number {
  const [agora, setAgora] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setAgora(Date.now()), ms);
    return () => clearInterval(t);
  }, [ms]);
  return agora;
}

/** Booleano guardado em localStorage (ex.: barra lateral recolhida). Sem storage, vale só na sessão. */
export function usePreferencia(chave: string, padrao: boolean): [boolean, (v: boolean) => void] {
  const [valor, setValor] = useState<boolean>(() => {
    try {
      const v = localStorage.getItem(chave);
      return v === null ? padrao : v === '1';
    } catch {
      return padrao;
    }
  });
  const gravar = (v: boolean) => {
    setValor(v);
    try {
      localStorage.setItem(chave, v ? '1' : '0');
    } catch {}
  };
  return [valor, gravar];
}

// ---------- título da página ----------

export interface PageMeta {
  title: string;
  /** Só texto: entra nas dependências do efeito (um nó React novo a cada render faria a página re-renderizar sem parar). */
  subtitle?: string;
}

/** A moldura (Shell) mostra o título e o subtítulo da página no cabeçalho; as páginas publicam via `usePageMeta`. */
export const PageMetaContext = createContext<{ meta: PageMeta | null; setMeta: (m: PageMeta | null) => void }>({
  meta: null,
  setMeta: () => {},
});

export function usePageMeta(title: string, subtitle?: string) {
  const { setMeta } = useContext(PageMetaContext);
  useEffect(() => {
    setMeta({ title, subtitle });
    return () => setMeta(null);
  }, [title, subtitle, setMeta]);
}
